import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  RadioGroup, FormControlLabel, Radio, Button,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ACTIVE_PRODUCT, INTEGRALDELTA } from '../config/productTarget';

/** The mode whose floater is pinned by a click rather than followed by the pointer. */
export const SPECIAL_STUDY_MODE = 'special';

const OPTIONS = [
  { value: 'ohlcv',        label: 'OHLCV',                          suffix: '' },
  { value: 'aspects_geo',  label: 'Planetary Aspects within Orb',   suffix: '(Geocentric)' },
  { value: 'aspects_helio',label: 'Planetary Aspects within Orb',   suffix: '(Heliocentric)' },
  { value: 'planets_sid',  label: "Planets' longitude, declination, distance and speed", suffix: '(Sidereal)' },
  { value: 'planets_trop', label: "Planets' longitude, declination, distance and speed", suffix: '(Tropical)' },
  { value: SPECIAL_STUDY_MODE, label: 'Special Study',              suffix: '(Click a bar)',
    adminOnly: true,
    hint: 'Two ranked criteria per viewpoint, geocentric and heliocentric, for the bar you click.' },
];

/**
 * ⚠ THE FOUR PLANETARY READOUTS ARE NOT OFFERED ON integralDelta.
 *
 * <p>Special Study goes too, and for the same reason rather than for its `adminOnly` flag: its own
 * hint says "geocentric and heliocentric", so it is an astrological readout under a neutral name.
 * What is left is OHLCV, which is the whole of what that product's floater has to say.
 */
const ASTRO_FLOATER_MODES = Object.freeze([
  'aspects_geo', 'aspects_helio', 'planets_sid', 'planets_trop', SPECIAL_STUDY_MODE,
]);

/**
 * The options this user may choose from, on this product.
 *
 * <h2>⚠ FILTERING THE DIALOG IS THE HALF THAT MATTERS</h2>
 *
 * The toolbar tooltip was changed to name OHLCV and RSI on integralDelta. Doing only that would
 * have been cosmetic in the worst way: a customer reads a neutral hover, clicks, and is offered
 * "Planetary Aspects within Orb (Heliocentric)" — the tooltip would have been the only thing
 * hiding it, and a tooltip is not a gate.
 *
 * <p>⚠ `product` IS OPTIONAL AND ITS ABSENCE IS THE OLD BEHAVIOUR, which is the permissive one
 * here. That is the opposite of the toolbar gates' default, and deliberate: this function's
 * existing callers and tests ask only about the admin flag, and silently emptying a dialog for
 * them would be a worse failure than showing one option too many. The one real caller passes it.
 */
export function optionsFor(isAdmin, product) {
  return OPTIONS
    .filter(o => !o.adminOnly || isAdmin)
    .filter(o => product !== INTEGRALDELTA || !ASTRO_FLOATER_MODES.includes(o.value));
}

/**
 * THE TOOLBAR HOVER FOR THIS CONTROL, by product.
 *
 * <h2>⚠ THE ASTRO TEXT NAMED THE DATA, WHICH IS THE WHOLE PROBLEM</h2>
 *
 * It read "OHLCV, Planetary Aspects, or Planet Positions" — so on integralDelta the hover itself
 * advertised that astrological readouts exist, which is exactly what the comment beside this
 * control already says a greyed-out button must not do. A tooltip is a smaller leak than a button
 * and it is the same kind.
 *
 * <p>⚠ `'OHLCV\n\nRSI'` IS LITERAL, INCLUDING THE BLANK LINE, and it mirrors what the floater
 * actually prints: `EChartComponent` builds the OHLCV rows and then, under the comment "THE
 * INDICATORS, after a blank line", appends RSI and MACD for the bars that have them. So the hover
 * describes the readout's real shape rather than summarising it — and the blank line only survives
 * because the Tooltip is given `whiteSpace: 'pre-line'`.
 *
 * <p>MACD is not named. It is in the readout and it is switched on far less often; naming both
 * would make a two-line hover into a list, and the list would then be wrong whenever either
 * indicator is off. OHLCV always shows; RSI is the one a reader of this product turns on.
 */
export const barFloaterTooltipFor = (product) => (product === INTEGRALDELTA
  ? 'OHLCV\n\nRSI'
  : 'Select what information to display when hovering over chart bars: '
    + 'OHLCV, Planetary Aspects, or Planet Positions');

/**
 * The accessible name, which is NOT the tooltip and must not be the blank-line string.
 *
 * <p>A screen reader announcing "OHLCV, newline, newline, RSI" is reading a layout. The label has
 * to say what the BUTTON does, so it keeps a sentence — with the astro nouns dropped on the
 * product that does not have them.
 */
export const barFloaterAriaLabelFor = (product) => (product === INTEGRALDELTA
  ? 'Bar readout: OHLCV and RSI'
  : 'Bar Floater Selection');

const BarFloaterSelectionDialog = ({
  open, onClose, currentMode, onApply, isAdmin = false, product = ACTIVE_PRODUCT,
}) => {
  const options = optionsFor(isAdmin, product);
  const [selected, setSelected] = useState(currentMode || 'ohlcv');

  // Sync with external prop when dialog opens.
  //
  // A mode this user may not choose is not carried into the dialog. Someone whose admin rights were
  // removed while Special Study was their selected mode would otherwise open a dialog with nothing
  // selected, and pressing Ok would apply the empty string.
  React.useEffect(() => {
    if (!open) return;
    const wanted = currentMode || 'ohlcv';
    setSelected(options.some(o => o.value === wanted) ? wanted : 'ohlcv');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentMode, isAdmin, product]);

  const handleOk = () => {
    onApply(selected);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2, px: 1 } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 500, fontSize: '1.2rem' }}>
        Bar Floater Selection
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <RadioGroup value={selected} onChange={e => setSelected(e.target.value)}>
          {options.map(opt => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio />}
              label={
                <span>
                  {opt.label}
                  {opt.suffix && <span style={{ fontWeight: 600, marginLeft: 16 }}>{opt.suffix}</span>}
                  {opt.hint && (
                    <span style={{ display: 'block', fontSize: 12, opacity: 0.7 }}>{opt.hint}</span>
                  )}
                </span>
              }
              sx={{ mb: 0.5 }}
            />
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button variant="outlined" onClick={handleOk}>Ok</Button>
      </DialogActions>
    </Dialog>
  );
};

BarFloaterSelectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentMode: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  /** The build target. Defaults to the baked one, so a caller that omits it still filters. */
  product: PropTypes.string,
};

export default BarFloaterSelectionDialog;
